import {observer} from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import {withAppStores} from "../../../../AppStores";
import {type FieldDataType} from "../../../../generated/api/base";
import type {AppState} from "../../../../data/state/AppState";
import {FieldInputsV5} from "../../../widgets/input/clicktoedit/InputUtils";
import type {IFieldAdapter} from "../../../../data/models/field/Field";
import type {IModel} from "../../../../data/models/Model";
import {colorPalette} from "../../styles/colorPalette";
import {ReactUtils} from "../../../utils/ReactUtils";
import {PillStyled, SelectInputStyled} from "../../input/select/SelectInputV5";
import {baseDistance, ELLIPSIS, FlexCenterStyle, fontWeight, InfoIconStyled, radius} from "../../styles/styles";
import {DateTimeInputStyled} from "../../input/datetime/DateTimeInputV5";
import {IconButtonStyled} from "../../interaction/IconButtonV5";
import {InitialsV5} from "../../widgets/InitialsV5";
import {Spinner, SpinnerStyled} from "../../loader/Spinner";
import {isNameField, isNameOrModelFieldEditable, isNameOrModelValid} from "../../../modules/abstract/sidepanel/tabs/details/field/FieldInputUtils";
import {isIconField} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {MoreButtonStyled, MultiLineInputStyled, MultiLineLabelStyled} from "./multi/MultiLineInputV5";
import {TextInputStyled, TextInputWrapperStyled, type IInputType} from "./TextInputV5";
import {SingleLineInputV5, SingleLineLabelStyled} from "./SingleLineInputV5";
import {MultiSelectInputStyled} from "./multi/MultiSelect.styled";
import {XyiconModelFieldStyled} from "./XyiconModelFieldV5";
import {PhoneInputStyled, PhoneLabelStyled} from "./PhoneInputV5";

interface IFieldInputProps {
	readonly dataType: FieldDataType;
	readonly dataTypeSettings: any;
	readonly value: any;
	readonly disabled?: boolean;
	readonly isUpdating?: boolean;
	readonly onFocusLossForceBlur?: boolean;
	readonly inputType?: IInputType;
	readonly dropdownIcon?: boolean;
	readonly field?: IFieldAdapter;
	readonly item?: IModel;
	readonly inline?: boolean;
	readonly appState?: AppState;
	readonly isInherited?: boolean;
	readonly onChange?: (value: any) => void;
	readonly onClick?: (event?: React.MouseEvent) => void;
	readonly getErrorMessage?: (value: string) => string;
}

@withAppStores("appState")
@observer
export class FieldInputV5 extends React.Component<IFieldInputProps> {
	public _containerRef = React.createRef<HTMLDivElement>();

	private _isItInsideDetailsContainer = false;

	override componentDidMount(): void {
		const detailsContainerElement = document.querySelector(".DetailsContainer");

		if (detailsContainerElement) {
			this._isItInsideDetailsContainer = detailsContainerElement.contains(this._containerRef.current);
		}
	}

	private getErrorMessage = (value: string) => {
		const {field, appState, item} = this.props;
		const name = value.trimEnd();

		if (isNameOrModelFieldEditable(item, field, appState)) {
			if (!name) {
				return "Name cannot be empty!";
			}

			if (!isNameOrModelValid(name, item, appState)) {
				return "Name needs to be unique!";
			}
		}
	};

	private saveRefIdToAppState(event: React.MouseEvent) {
		const {field, appState, onClick} = this.props;

		if (this._isItInsideDetailsContainer === appState.isDetailsContainerOpened) {
			appState.selectedFieldInputRefId = field?.refId || "";
		}

		onClick?.(event);
	}

	private getEditInput() {
		const {
			value,
			dataType,
			dataTypeSettings,
			disabled,
			isUpdating = false,
			onFocusLossForceBlur,
			inputType,
			field,
			dropdownIcon,
			item,
			isInherited,
			inline,
			onChange,
			getErrorMessage,
		} = this.props;

		if (isIconField(field)) {
			return (
				<InitialsV5
					item={item}
					thumbnailSize={32}
					hideIsFavouriteDogear={true}
				/>
			);
		}

		let InputComponent = FieldInputsV5[dataType];

		InputComponent = InputComponent || SingleLineInputV5;

		return (
			<>
				<InputComponent
					field={field}
					item={item}
					value={value}
					dataTypeSettings={dataTypeSettings}
					disabled={disabled || isUpdating}
					inputType={inputType}
					onFocusLossForceBlur={onFocusLossForceBlur}
					focused={false}
					onChange={onChange}
					onClick={this.onClick}
					getErrorMessage={getErrorMessage ?? this.getErrorMessage}
					isSameWidth={true}
					noBooleanLabel={false}
					dropdownIcon={dropdownIcon}
					isInherited={isInherited}
					inline={inline}
					updateLastValidOnInput={!isNameField(item, field)}
				/>
				{isUpdating && <Spinner isYellow={field?.hasValidation} />}
			</>
		);
	}

	private onClick = (e: React.MouseEvent) => {
		if (this.props.field?.refId !== this.props.appState.selectedFieldInputRefId) {
			if (e) {
				e.stopPropagation();
			}

			this.saveRefIdToAppState(e);
		}

		this.props.onClick?.(e);
	};

	public override render() {
		const {inline, isInherited, disabled} = this.props;

		return (
			<FieldInputStyled
				style={{display: "contents"}}
				className={ReactUtils.cls({inline, disabled, isInherited})}
				ref={this._containerRef}
			>
				{this.getEditInput()}
			</FieldInputStyled>
		);
	}
}

export const FieldInputStyled = styled.div`
	${SpinnerStyled} {
		position: absolute;
		top: 8px;
		right: 7px;
	}

	.lastModifiedBy {
		${ELLIPSIS}
		padding: 8px;
		padding-top: 11px;
	}

	&.disabled {
		${MultiSelectInputStyled}, ${MultiLineLabelStyled} ${MoreButtonStyled} {
			pointer-events: all;
		}
	}

	${TextInputStyled}, ${MultiLineLabelStyled}, ${DateTimeInputStyled}, ${PhoneLabelStyled}, .numberInput {
		cursor: text;
		font-weight: ${fontWeight.normal};
	}

	${SelectInputStyled}, ${MultiSelectInputStyled}, ${XyiconModelFieldStyled}, .boolean {
		cursor: pointer;
	}

	${TextInputWrapperStyled} ${InfoIconStyled} {
		background-color: ${colorPalette.white};
	}

	&:not(.inline) {
		.field-input-container,
		${TextInputStyled}, ${MultiLineLabelStyled}, ${DateTimeInputStyled}, ${PhoneLabelStyled}, .numberInput,
		${SingleLineLabelStyled} {
			width: 100%;
		}

		${TextInputStyled}, ${SingleLineLabelStyled}, ${MultiLineLabelStyled}, ${DateTimeInputStyled}, ${SelectInputStyled}, ${MultiSelectInputStyled}, ${PhoneLabelStyled}, .numberInput {
			border-radius: ${baseDistance.xs};
			min-height: 32px;
			${ELLIPSIS}

			&:not(.isInherited) {
				border: 1px solid ${colorPalette.gray.c300};
				&:not(:focus):hover {
					background-color: ${colorPalette.gray.c200Light};
				}
			}

			&:focus {
				background-color: ${colorPalette.white};
				border: 1px solid ${colorPalette.primary.c500Primary};
			}
		}

		${SelectInputStyled}.isOpen, ${MultiSelectInputStyled}.isActive, ${DateTimeInputStyled}.open, .boolean.isActive {
			background-color: ${colorPalette.white};
			border: 1px solid ${colorPalette.primary.c500Primary};
		}

		${DateTimeInputStyled} {
			${TextInputStyled} {
				min-height: 30px;
				height: 30px;
			}

			${SingleLineLabelStyled} {
				border: none;
			}

			${IconButtonStyled} {
				height: 30px;
			}
		}

		.boolean {
			border: solid 1px transparent;
			border-radius: ${baseDistance.xs};
			width: 100%;
			padding: 0 ${baseDistance.xs};
		}

		.multiline {
			width: 100%;
			border: 1px solid ${colorPalette.primary.c500Primary};
			border-radius: ${baseDistance.xs};
			background-color: ${colorPalette.white};
		}

		.numberInput {
			height: 32px;
			padding-left: ${baseDistance.sm};
		}

		${PhoneInputStyled} {
			width: 100%;

			${SelectInputStyled} {
				border: 1px solid ${colorPalette.primary.c500Primary};
				border-radius: ${baseDistance.xs} 0 0 ${baseDistance.xs};
				border-right: 1px solid ${colorPalette.gray.c300};
				color: ${colorPalette.gray.c700Dark};
				gap: ${baseDistance.xs};
				width: auto;
				flex-shrink: 0;

				& > span {
					overflow: initial;

					& + svg {
						min-width: ${baseDistance.sm};
					}
				}
			}

			.numberInput {
				border: 1px solid ${colorPalette.primary.c500Primary};
				border-radius: 0 ${baseDistance.xs} ${baseDistance.xs} 0;
				border-left: none;
			}
		}

		&.disabled {
			${SingleLineLabelStyled}, ${MultiLineLabelStyled}, ${DateTimeInputStyled}, ${SelectInputStyled}:not(.isInherited), ${MultiSelectInputStyled}:not(.isInherited), ${PhoneLabelStyled}, .numberInput {
				color: ${colorPalette.gray.c700Dark};
				border: none;
				background-color: transparent;
			}
		}
	}

	&.inline {
		> *:not(${SpinnerStyled}) {
			border: none;
			min-height: 40px;
			background-color: transparent;
		}

		${SpinnerStyled} {
			top: 10px;
		}

		${SingleLineLabelStyled}, ${TextInputStyled}, ${MultiLineLabelStyled}, ${DateTimeInputStyled}, ${SelectInputStyled}, ${MultiSelectInputStyled}, ${PhoneLabelStyled}, .numberInput, .boolean {
			border: 2px solid transparent;
			min-height: 40px;
			border-radius: ${radius.xs};
			${ELLIPSIS};
			box-sizing: border-box;

			&:hover {
				background-color: ${colorPalette.gray.c200Light};
				border-color: ${colorPalette.primary.c500Primary};
			}

			&:focus {
				background-color: ${colorPalette.white};
				border: 2px solid ${colorPalette.primary.c500Primary};

				&::selection {
					background-color: #fbb700;
					color: ${colorPalette.white};
				}
			}
		}

		// basically the SingleSelect, MultiSelect and DateTime focus state
		${SelectInputStyled}.isOpen, ${MultiSelectInputStyled}.isActive, ${DateTimeInputStyled}.open, .boolean.isActive {
			background-color: ${colorPalette.white};
			border: 2px solid ${colorPalette.primary.c500Primary};
		}

		${DateTimeInputStyled} {
			${TextInputStyled} {
				min-height: 38px;
				height: 38px;
			}

			${IconButtonStyled} {
				min-width: 24px;
				height: 24px;
				background-color: ${colorPalette.gray.c200Light};
			}

			&.open ${IconButtonStyled} {
				background-color: ${colorPalette.white};
			}
		}

		${MultiLineInputStyled} {
			width: 100%;
			border: 2px solid ${colorPalette.primary.c500Primary};
			border-radius: ${radius.xs};
			background-color: ${colorPalette.white};

			textarea::selection {
				background-color: #fbb700;
				color: ${colorPalette.white};
			}
		}

		.boolean {
			border: 2px solid transparent;
			padding-left: ${baseDistance.sm};
		}

		${MultiSelectInputStyled} {
			${FlexCenterStyle};
		}

		.numberInput {
			transition: background-color 0.4s ease;
			background-color: transparent;
			padding-left: ${baseDistance.sm};
		}

		${SelectInputStyled}.disabled {
			svg {
				visibility: hidden;
			}

			${PillStyled} {
				svg {
					visibility: visible;
				}
			}
		}

		${PhoneLabelStyled} {
			.countryCode {
				padding-right: 24px;
			}
		}

		${PhoneInputStyled} {
			${SelectInputStyled} {
				border: 2px solid ${colorPalette.primary.c500Primary};
				border-radius: ${radius.xs} 0 0 ${radius.xs};
				border-right: 1px solid ${colorPalette.primary.c500Primary};
			}
			${TextInputStyled} {
				border: 2px solid ${colorPalette.primary.c500Primary};
				border-radius: 0 2px 2px 0;
				border-left: none;
			}
		}

		&.disabled {
			${XyiconModelFieldStyled}, ${TextInputStyled}, ${MultiLineLabelStyled}, ${DateTimeInputStyled}, ${SelectInputStyled}, ${MultiSelectInputStyled}, ${PhoneLabelStyled}, .numberInput, .boolean {
				border: none;
				background-color: transparent;
				pointer-events: none;
				cursor: auto;
			}

			.numberInput {
				color: ${colorPalette.gray.c700Dark};
			}

			.boolean:hover {
				background-color: transparent;
				border-color: transparent;
			}

			${MultiSelectInputStyled}, ${MultiLineLabelStyled} ${MoreButtonStyled} {
				pointer-events: auto;
			}
		}

		${InfoIconStyled} {
			top: 7px;
		}

		.Initials {
			&.thumbnail {
				padding: 2px;
			}
		}
	}

	${DateTimeInputStyled}.open.empty {
		${IconButtonStyled}:not(.cancel) {
			background-color: ${colorPalette.primary.c200Light};
			visibility: visible;

			svg {
				color: ${colorPalette.primary.c500Primary};
			}
		}
	}
`;
